import PropTypes from "prop-types";
import { useState } from "react";
import { useThemeContext } from "../../context/themeContext";
import { Link } from "react-router-dom";

export default function Button({
  variant = "primary",
  status,
  children,
  onClick,
  className,
  submit,
  href,
  disabled,
  target,
}) {
  const [isLoading, setIsLoading] = useState(false); // Track loading state
  let buttonColor = "";
  let buttonVariant = "";

  const { theme } = useThemeContext();

  // Set button variant based on variant prop
  switch (variant) {
    case "secondary":
      buttonVariant =
        "p-3 bg-sportsBlue text-white rounded-1 hover:shadow-md active:shadow-md";
      break;
    case "tertiary":
      buttonVariant =
        "p-3 bg-sportsLightGrey text-sportsBlack rounded-1 hover:shadow-md active:shadow-md";
      break;
    case "quaternary":
      buttonVariant = `p-3 ${
        theme === "staff"
          ? "border-sportsBlue "
          : "border-sportsYellow"
      } rounded-1 hover:shadow-md active:shadow-md font-semibold border-2 bg-white transition-all text-black`;
      break;
    default:
      buttonVariant = `p-3 ${
        theme === "staff"
          ? "bg-sportsBlue text-white"
          : "bg-sportsYellow text-sportsBlack"
      } rounded-1 hover:shadow-md active:shadow-md font-semibold`;
  }

  // Set button color based on status prop
  switch (status) {
    case "null":
      break;
    case "activation":
      buttonColor = "!bg-sportsDarkRed text-white";
      break;
    case "incident":
      buttonColor = "!bg-sportsRed text-white";
      break;
    case "emergency":
      buttonColor = "px-5 py-9 !bg-sportsRed text-white";
      break;
    case "nobg":
      buttonColor = `!p-0 bg-transparent text-sportsBlack underline rounded-none hover:shadow-none active:shadow-none`;
      break;
    case "nobg-red":
      buttonColor = `!p-0 bg-transparent text-sportsRed underline rounded-none hover:shadow-none active:shadow-none`;
      break;
    case "disabled":
      buttonColor = `opacity-50 hover:shadow-none active:shadow-none`;
      break;
  }

  if (disabled) {
    buttonColor = "opacity-50 hover:shadow-none active:shadow-none";
  }

  const handleClick = async (e) => {
    if (onClick) {
      setIsLoading(true); // Disable the button when action starts
      try {
        await onClick(e); // Execute the onClick action
      } finally {
        setIsLoading(false); // Re-enable the button once action is complete
      }
    }
  };

  const buttonClasses = `text-base leading-6 flex flex-row gap-[10px] justify-center items-center ${isLoading ? 'opacity-75 cursor-not-allowed' : ''} ${disabled ? 'cursor-not-allowed' : ''} ${buttonVariant} ${buttonColor} ${className}`;

  if (href) {
    return (
      <Link to={href} target={target && target} className="flex flex-row w-full">
        <button
          onClick={handleClick}
          type={submit ? "submit" : "button"}
          disabled={isLoading || disabled} // Disable if loading or manually disabled
          className={buttonClasses}
        >
          {children}
        </button>
      </Link>
    );
  }

  return (
    <button
      onClick={handleClick}
      disabled={isLoading || disabled} // Disable if loading or manually disabled
      type={submit ? "submit" : "button"}
      className={buttonClasses}
    >
      {children}
    </button>
  );
}

Button.propTypes = {
  variant: PropTypes.oneOf(["primary", "secondary", "tertiary", "quaternary"]),
  status: PropTypes.oneOf([
    "activation",
    "incident",
    "emergency",
    "nobg",
    "nobg-red",
    "disabled",
    "null",
  ]),
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func,
  className: PropTypes.string,
  submit: PropTypes.bool,
};
